/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { StorageAdapter } from "../storage";
import { STORAGE_KEY_SERVICE_API_LOGGING_WINDOW } from "../storage/storageConfig";
import { Log } from "./Log";
import { LoggingWindow } from "./LoggingWindow";

/**
 * Persisted representation of a Service-API logging window.
 */
interface TServiceApiLoggingWindowRecord {
  startEpochSec: number;
  endEpochSec: number;
}

/**
 * Durable persistence for the Service-API logging window so that the
 * window is honoured across app restarts until it naturally expires.
 * Values are non-PII runtime config and stored in the plain
 * (non-checksummed) cache.
 */
export class ServiceApiLoggingConfigStore {
  private static readonly TAG: string = "ServiceApiLoggingConfigStore";

  /**
   * Persists the window, or clears any stored window when null.
   *
   * @param window - The window to persist, or null to clear
   */
  public static async saveWindow(window: LoggingWindow | null): Promise<void> {
    try {
      const storage: StorageAdapter = StorageAdapter.getInstance();
      if (window === null) {
        await storage.delete(STORAGE_KEY_SERVICE_API_LOGGING_WINDOW);
        return;
      }

      const record: TServiceApiLoggingWindowRecord = {
        startEpochSec: window.getStartEpochSec(),
        endEpochSec: window.getEndEpochSec()
      };
      await storage.set(
        STORAGE_KEY_SERVICE_API_LOGGING_WINDOW,
        record,
        Number.POSITIVE_INFINITY
      );
    } catch (error: unknown) {
      Log.w(ServiceApiLoggingConfigStore.TAG, "saveWindow()", error);
    }
  }

  /**
   * Loads the persisted window. Returns null (and clears the stored
   * value) when nothing is stored or the stored bounds are invalid.
   *
   * @returns The persisted window, or null
   */
  public static async loadWindow(): Promise<LoggingWindow | null> {
    try {
      const storage: StorageAdapter = StorageAdapter.getInstance();
      const record: TServiceApiLoggingWindowRecord | null =
        await storage.get<TServiceApiLoggingWindowRecord>(
          STORAGE_KEY_SERVICE_API_LOGGING_WINDOW
        );
      if (record === null) {
        return null;
      }

      const window: LoggingWindow | null = LoggingWindow.fromEpochSeconds(
        record.startEpochSec,
        record.endEpochSec
      );
      if (window === null) {
        await storage.delete(STORAGE_KEY_SERVICE_API_LOGGING_WINDOW);
      }
      return window;
    } catch (error: unknown) {
      Log.w(ServiceApiLoggingConfigStore.TAG, "loadWindow()", error);
      return null;
    }
  }
}
